import { Controller } from "stimulus"

// Loading and failure feedback for the post editor's asynchronous right-sidebar frames.
//
// Every panel in that column resolves after the editor page has painted: the `ai_chat`
// frame fetches the chat card, `interact` fetches its contents, and the Chat/Settings tabs
// re-fetch `chat-card-body`. Each frame renders a server-side placeholder, so the column
// keeps its final shape while the request is unresolved.
//
// Turbo only reports some failures: a completed response with a non-HTML body (for example
// a plain-text 503) is ignored entirely - no `turbo:frame-missing`, no render - which would
// leave the placeholder on screen forever. This controller therefore inspects the completed
// response itself (`turbo:before-fetch-response`) regardless of content type, and replaces the
// placeholder with a visible, recoverable error state when the response did not succeed.
export default class extends Controller {
  static values = { label: String, placeholderRoutes: Object }

  connect() {
    this.frame = this.element
    this.lastUrl = null
    this.failedStatus = null
    this.onRequest = this.onRequest.bind(this)
    this.onResponse = this.onResponse.bind(this)
    this.onFinished = this.onFinished.bind(this)
    this.onMissing = this.onMissing.bind(this)
    this.onRequestError = this.onRequestError.bind(this)
    this.frame.addEventListener("turbo:before-fetch-request", this.onRequest)
    this.frame.addEventListener("turbo:before-fetch-response", this.onResponse)
    this.frame.addEventListener("turbo:frame-render", this.onFinished)
    this.frame.addEventListener("turbo:frame-load", this.onFinished)
    this.frame.addEventListener("turbo:frame-missing", this.onMissing)
    this.frame.addEventListener("turbo:fetch-request-error", this.onRequestError)
    // Busy tracks a request that is actually pending, not the presence of placeholder markup.
    // A frame with a `src` starts fetching as soon as it is connected (which can be before Turbo
    // renders this controller) and Turbo sets `complete` only once that frame really rendered, so
    // `src` without `complete` is a pending load and the panel is genuinely busy. A src-less frame
    // (the Chat tab before its first tab navigation, or a frame restored from the Turbo page
    // cache) has nothing in flight: it must not claim to be busy just because a placeholder or a
    // previous render is in its markup. Turbo reports nothing at all for a non-HTML body, so an
    // eager frame whose request failed before this controller connected is re-checked below.
    if (this.frame.getAttribute("src") && this.frame.hasAttribute("busy")) {
      // Turbo can start a reconnected frame's request before Stimulus reattaches its listeners.
      // Replace stale loaded content with the durable server skeleton even in that race.
      this.showPlaceholder()
      this.earlyFailureTimeout = window.setTimeout(() => this.checkMissedFailure(), 250)
    } else if (this.frame.getAttribute("src") && this.frame.hasAttribute("complete") && !this.frame.innerHTML.trim()) {
      // Turbo's page/history restoration can reconnect a completed src frame after its nested
      // content was discarded. Treat that blank cache entry as unresolved and fetch it again.
      this.showPlaceholder()
      this.frame.reload()
    } else if (this.frame.getAttribute("src") && !this.frame.hasAttribute("complete")) {
      this.showPlaceholder()
      if (!this.isDeferred()) {
        this.earlyFailureTimeout = window.setTimeout(() => this.checkMissedFailure(), 250)
      }
    } else if (!this.frame.hasAttribute("busy")) {
      this.clearBusy()
    }
  }

  checkMissedFailure() {
    // Turbo clears `busy` before its asynchronous frame render finishes. A successful response
    // received during that gap is not a missed failure, even if `complete` is not set yet.
    if (this.frame.hasAttribute("busy") || this.frame.hasAttribute("complete") || this.responseReceived) return
    if (this.frame.dataset.sidebarFrameResolved === "true") return
    if (this.frame.querySelector(":scope > .sidebar-frame-error")) return
    this.showFailure(0)
  }

  isDeferred() {
    return this.frame.getAttribute("loading") === "lazy"
  }

  disconnect() {
    window.clearTimeout(this.earlyFailureTimeout)
    this.frame.removeEventListener("turbo:before-fetch-request", this.onRequest)
    this.frame.removeEventListener("turbo:before-fetch-response", this.onResponse)
    this.frame.removeEventListener("turbo:frame-render", this.onFinished)
    this.frame.removeEventListener("turbo:frame-load", this.onFinished)
    this.frame.removeEventListener("turbo:frame-missing", this.onMissing)
    this.frame.removeEventListener("turbo:fetch-request-error", this.onRequestError)
  }

  onRequest(event) {
    if (event.target !== this.frame) return
    window.clearTimeout(this.earlyFailureTimeout)
    this.earlyFailureTimeout = null
    this.lastUrl = event.detail?.url?.toString() || this.lastUrl
    this.failedStatus = null
    this.showPlaceholder()
  }

  onResponse(event) {
    if (event.target !== this.frame) return
    const response = event.detail?.fetchResponse
    if (!response) return
    this.responseReceived = true
    window.clearTimeout(this.earlyFailureTimeout)
    this.earlyFailureTimeout = null
    if (response.succeeded) return
    this.failedStatus = response.statusCode
    // Turbo only reports a failed frame response when its body is HTML: a plain-text 503
    // produces no `turbo:frame-missing`, no render and no `turbo:frame-load`, so without this
    // the placeholder would stay on screen forever. Report it as soon as the response lands.
    this.showFailure(response.statusCode)
  }

  onFinished(event) {
    if (event.target !== this.frame) return
    this.responseReceived = true
    window.clearTimeout(this.earlyFailureTimeout)
    this.earlyFailureTimeout = null
    if (this.failedStatus) {
      this.showFailure(this.failedStatus)
      return
    }
    this.markResolved()
  }

  onMissing(event) {
    if (event.target !== this.frame) return
    this.responseReceived = true
    // Handled here: Turbo's default is a console error, and its fallback is a full-page visit.
    event.preventDefault()
    this.showFailure(event.detail?.response?.status || 0)
  }

  onRequestError(event) {
    if (event.target !== this.frame) return
    this.responseReceived = true
    event.preventDefault()
    this.showFailure(0)
  }

  retry(event) {
    event?.preventDefault()
    const url = this.frame.getAttribute("src") || this.lastUrl
    this.failedStatus = null
    this.removeFailure()
    this.showPlaceholder()
    if (this.frame.getAttribute("src")) {
      this.frame.reload()
    } else if (url) {
      this.frame.setAttribute("src", url)
    }
  }

  // A frame that loads different panels (Chat vs Settings) can map a URL fragment to its own
  // placeholder, so the skeleton matches the panel being loaded rather than the default one.
  placeholderTemplate() {
    const url = this.lastUrl || ""
    const routed = Object.entries(this.placeholderRoutesValue).find(([fragment]) => url.includes(fragment))
    const id = routed ? routed[1] : this.element.dataset.sidebarFramePlaceholderId
    return id ? document.getElementById(id) : null
  }

  showPlaceholder() {
    this.responseReceived = false
    const template = this.placeholderTemplate()
    this.removeFailure()
    delete this.frame.dataset.sidebarFrameResolved
    if (template) {
      // This frame may contain nested frames with their own placeholders. Always restore this
      // frame's durable sibling template instead of mistaking a descendant skeleton for its own.
      this.frame.replaceChildren(template.content.cloneNode(true))
    }
    this.markBusy()
  }

  markBusy() {
    this.frame.setAttribute("aria-busy", "true")
    this.frame.classList.add("sidebar-frame-loading")
  }

  clearBusy() {
    this.frame.setAttribute("aria-busy", "false")
    this.frame.classList.remove("sidebar-frame-loading")
  }

  markResolved() {
    this.clearBusy()
    this.frame.dataset.sidebarFrameResolved = "true"
    this.removeFailure()
  }

  removeFailure() {
    this.frame.querySelector(":scope > .sidebar-frame-error")?.remove()
  }

  label() {
    return this.labelValue || "this panel"
  }

  showFailure(status) {
    this.responseReceived = true
    window.clearTimeout(this.earlyFailureTimeout)
    this.earlyFailureTimeout = null
    this.frame.setAttribute("aria-busy", "false")
    this.frame.classList.remove("sidebar-frame-loading")
    const detail = status > 0
      ? `The server responded with ${status}.`
      : "The request could not be completed."
    const error = document.createElement("div")
    error.className = "sidebar-frame-error"
    error.setAttribute("role", "status")
    error.innerHTML = `
      <span class="fal fa-exclamation-triangle sidebar-frame-error-icon" aria-hidden="true"></span>
      <p class="sidebar-frame-error-title">Couldn't load ${escapeHtml(this.label())}.</p>
      <p class="sidebar-frame-error-detail">${detail} Check your connection and try again.</p>
      <button type="button" class="btn btn-sm btn-5 sidebar-frame-error-retry" data-action="sidebar-frame#retry">Retry</button>
    `
    this.removeFailure()
    this.frame.replaceChildren(error)
  }
}

function escapeHtml(text) {
  return String(text).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
}
